import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { applyLoadingBackground } from '../utils/loadingBackground';
import {
    getOrCreateAccountId,
    setAccountId,
    getAccessToken,
    saveTokens,
    resolveLegalPersonFromUrl,
    saveLegalPersonId,
} from '../utils/accountStorage';
import WebLogin from '../components/WebLogin';

const UserContext = createContext();
const apiUrl = process.env.REACT_APP_API_URL
    || (typeof window !== 'undefined' ? `${window.location.origin}/api` : 'http://localhost:8000/api');

const withApiPrefix = (baseUrl) => {
    const normalized = String(baseUrl || '').replace(/\/+$/, '');
    if (normalized.endsWith('/api')) return normalized;
    return `${normalized}/api`;
};

const fetchWithApiFallback = async (baseUrl, path, options) => {
    const primary = `${String(baseUrl || '').replace(/\/+$/, '')}${path}`;
    const token = getAccessToken();
    const headers = {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    const opts = { ...options, headers: { ...headers, ...options.headers } };

    let response = await fetch(primary, opts);
    if (response.status !== 404) return response;
    const fallbackBase = withApiPrefix(baseUrl);
    const fallback = `${fallbackBase}${path}`;
    if (fallback === primary) return response;
    return fetch(fallback, opts);
};

const getDeviceId = () => {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
        deviceId = uuidv4();
        localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
};

async function fetchAuthTokens(accountId, deviceId) {
    const res = await fetchWithApiFallback(apiUrl, '/auth/token', {
        method: 'POST',
        body: JSON.stringify({ accountId, deviceId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Не удалось получить токен');
    if (data.accessToken) saveTokens(data.accessToken, data.refreshToken);
    return data;
}

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [needsLogin, setNeedsLogin] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);
    const [registrationAllowed, setRegistrationAllowed] = useState(false);
    const [verification, setVerification] = useState(false);
    const [tempUser, setTempUser] = useState(null);
    const [error, setError] = useState(null);
    const [deviceId, setDeviceId] = useState(null);
    const hasInitialized = useRef(false);
    const [blockReason, setBlockReason] = useState(null);
    const [isLocked, setIsLocked] = useState(() => localStorage.getItem('appLockEnabled') === 'true');

    const unlockApp = () => setIsLocked(false);

    const blockSession = useCallback((reason) => {
        const text = reason && String(reason).trim() ? String(reason).trim() : 'Аккаунт заблокирован.';
        setAccessDenied(true);
        setBlockReason(text);
        setUser(null);
        setRegistrationAllowed(false);
        setVerification(false);
        setTempUser(null);
        setError(null);
        localStorage.removeItem('appLockEnabled');
        setIsLocked(false);
    }, []);

    const bootstrapAccount = useCallback(async (accountIdOverride) => {
        const accountId = accountIdOverride || getOrCreateAccountId();
        setAccountId(accountId);
        const storedDeviceId = getDeviceId();
        setDeviceId(storedDeviceId);
        const legalPersonForApi = resolveLegalPersonFromUrl() || undefined;

        const body = {
            accountId,
            telegramId: accountId,
            firstName: '',
            lastName: '',
            username: accountId,
            deviceId: storedDeviceId,
        };
        if (legalPersonForApi) body.legalPerson = legalPersonForApi;

        const userResponse = await fetchWithApiFallback(apiUrl, '/front/users', {
            method: 'POST',
            body: JSON.stringify(body),
        });

        if (userResponse.status === 409) {
            const errorData = await userResponse.json().catch(() => ({}));
            setError(errorData.error);
            setAccessDenied(true);
            return;
        }

        if (!userResponse.ok) {
            const errorData = await userResponse.json().catch(() => ({}));
            throw new Error(errorData.error || userResponse.statusText);
        }

        const userData = await userResponse.json();

        if (userData.accessToken) {
            saveTokens(userData.accessToken, userData.refreshToken);
        } else {
            await fetchAuthTokens(accountId, storedDeviceId);
        }

        const lpBgUrl =
            userData.legalPersonMiniAppBackgroundUrl?.trim?.() ||
            userData.user?.legalPersonMiniAppBackgroundUrl?.trim?.() ||
            '';
        if (lpBgUrl) applyLoadingBackground(lpBgUrl);

        if (userData.isBlocked) {
            setAccessDenied(true);
            setBlockReason(userData.blockReason || 'Аккаунт заблокирован.');
            return;
        }

        const profile = { id: accountId, first_name: '', last_name: '', username: accountId };

        if (userData.exists) {
            const u = { ...(userData.user || {}) };
            u.permissions = (Number(u.permissions) || 0) >>> 0;
            setUser(u);
            setNeedsLogin(false);
            if (userData.user?.appLock?.enabled) {
                localStorage.setItem('appLockEnabled', 'true');
                setIsLocked(true);
            } else {
                localStorage.removeItem('appLockEnabled');
                setIsLocked(false);
            }
        } else if (userData.registrationAllowed) {
            setTempUser(profile);
            setRegistrationAllowed(true);
            setNeedsLogin(false);
        } else if (userData.verification) {
            setVerification(true);
            setRegistrationAllowed(true);
            setTempUser(profile);
            setNeedsLogin(false);
        } else {
            setAccessDenied(true);
            setBlockReason(userData.blockReason);
        }

        await fetchWithApiFallback(apiUrl, '/front/events', {
            method: 'POST',
            body: JSON.stringify({
                eventType: 'login_attempt',
                description: `Пользователь ${accountId} открыл веб-приложение.`,
                userId: accountId,
                userName: accountId,
            }),
        }).catch(() => {});
    }, []);

    const resolveRegistrationPin = useCallback(async (pin) => {
        const res = await fetchWithApiFallback(apiUrl, '/front/registration/resolve-pin', {
            method: 'POST',
            body: JSON.stringify({ pass: pin }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'PIN не найден');
        saveLegalPersonId(data.legalPersonId);
        return data;
    }, []);

    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const token = getAccessToken();

        if (!token) {
            setNeedsLogin(true);
            setLoading(false);
            return;
        }

        (async () => {
            try {
                await bootstrapAccount(getOrCreateAccountId());
            } catch (e) {
                setNeedsLogin(true);
                setBlockReason(e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [bootstrapAccount]);

    if (loading) {
        return (
            <UserContext.Provider value={{ user: null, loading: true }}>
                <div className="loading">Загрузка…</div>
            </UserContext.Provider>
        );
    }

    if (needsLogin) {
        return (
            <UserContext.Provider value={{ user: null, loading: false, needsLogin: true }}>
                <WebLogin
                    onResolvePin={resolveRegistrationPin}
                    onContinue={async (id) => {
                        setLoading(true);
                        setNeedsLogin(false);
                        try {
                            await bootstrapAccount(id || getOrCreateAccountId());
                        } catch (e) {
                            setNeedsLogin(true);
                            setError(e.message);
                        } finally {
                            setLoading(false);
                        }
                    }}
                />
            </UserContext.Provider>
        );
    }

    return (
        <UserContext.Provider value={{
            user, setUser, loading, accessDenied, registrationAllowed, verification,
            tempUser, deviceId, blockReason, isLocked, unlockApp, blockSession, error,
        }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);
